/**
 * Typed HoloXI API client.
 *
 * The browser is the control plane: this module only reads state and sends
 * lifecycle intents. It never holds run state, and nothing here keeps a
 * process alive.
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

export type RunState =
  | "PENDING" | "STARTING" | "RUNNING" | "DEGRADED"
  | "STOPPING" | "COMPLETED" | "FAILED" | "CANCELLED";

export interface Workspace { id: string; name: string; slug: string; role?: string }
export interface User { id: string; email: string; full_name: string }
export interface Session { access_token: string; user: User; workspaces: Workspace[] }

export interface Source {
  id: string; name: string; description: string; kind: string; adapter: string;
  status: "ONLINE" | "OFFLINE" | "UNKNOWN" | "ERROR";
  config: Record<string, unknown>; has_credentials: boolean;
  width: number | null; height: number | null; fps: number | null;
  tags: string[]; device_id: string | null; location: string;
  firmware_version: string; active_runs: number; health: Record<string, unknown>;
}

export interface ParamSpec {
  key: string; label: string; type: string; default: unknown;
  min?: number; max?: number; options?: string[]; help?: string; advanced?: boolean;
}

export interface AppManifest {
  app_id: string; name: string; description: string; version: string; category: string;
  compute: string; requires: string[]; requires_model: boolean;
  consumes: string[]; produces: string[]; visualization: string;
  params: ParamSpec[]; output_schema: { name: string; type: string; description: string }[];
  storage_capabilities: string[]; default_processing_fps: number;
  available: boolean; missing_requirements: string[];
}

export interface NormRect { x: number; y: number; width: number; height: number }

export interface SinkConfig {
  kind: "csv" | "jsonl" | "database" | "image" | "video" | "mask";
  enabled?: boolean; rotation?: "none" | "time" | "size" | "rows";
  rotate_seconds?: number; rotate_rows?: number; consolidate_on_stop?: boolean;
}

export interface AppNode {
  node_id: string; app_id: string; app_version?: string; label?: string; enabled?: boolean;
  processing_fps?: number | null; model_id?: string | null;
  params: Record<string, unknown>; regions: NormRect[]; region_names: string[];
  lines?: { name: string; a: { x: number; y: number }; b: { x: number; y: number } }[];
  sinks: SinkConfig[];
}

export interface ProcessGraph {
  name: string; description?: string; tags?: string[];
  source: { source_id: string; reconnect_interval_s?: number; max_reconnect_attempts?: number };
  nodes: AppNode[];
  connections: { from_node: string; to_node: string; channel?: string }[];
  triggers?: { when: string; action: string; from_node?: string; target_node?: string }[];
  runtime?: Record<string, unknown>;
}

export interface RunSummary {
  id: string; process_id: string; process_version: number; state: RunState;
  state_reason: string; source_id: string | null; holopod_id: string | null;
  sync_session_id: string | null; worker_id: string | null;
  started_at: string | null; stopped_at: string | null;
  duration_seconds: number; last_heartbeat_at: string | null; restart_count: number;
}

export interface RunApp {
  node_id: string; app_id: string; app_version: string; state: string; state_reason: string;
  rows_written: number; frames_processed: number;
  last_result: Record<string, unknown>; metrics: Record<string, unknown>;
  model_version: string | null;
}

export interface RunDetail extends RunSummary {
  process_name: string; config_snapshot: ProcessGraph;
  metrics: Record<string, any>; statistics: Record<string, any>;
  finalization: Record<string, any>; error_log: { at: string; message: string }[];
  storage_prefix: string; apps: RunApp[];
  artifact_summary: Record<string, { count: number; bytes: number }>;
  first_frame_at: string | null; requested_start_at: string | null;
}

export interface ProcessSummary {
  id: string; name: string; description: string; tags: string[]; status: string;
  current_version: number; source_id: string | null; source_name: string | null;
  owner_id: string; app_ids: string[]; graph: ProcessGraph;
  created_at: string; updated_at: string;
  last_run: RunSummary | null; active_run: RunSummary | null;
}

export interface Artifact {
  id: string; run_id: string; process_id?: string | null; kind: string; filename: string; storage_key: string;
  content_type: string; size_bytes: number; rows: number | null; sequence: number;
  node_id: string | null; app_id: string | null; finalized: boolean;
  created_at: string; download_url: string;
}

export interface EventRow {
  id: number; event_type: string; severity: string; message: string; timestamp: string;
  run_id: string | null; process_id: string | null; app_id: string | null;
  meta: Record<string, unknown>;
}

export interface WorkerRow {
  id: string; hostname: string; status: string; backend: string;
  capabilities: { cpu_cores?: number; ram_mb?: number; gpu?: boolean; gpu_name?: string; vram_mb?: number; runtimes?: string[] };
  active_runs: number; max_runs: number; last_heartbeat_at: string | null;
}

export interface Dashboard {
  sources: Record<string, number>;
  processes: Record<string, number>;
  runs: Record<string, number>;
  compute: Record<string, number>;
  storage: { bytes_used: number; quota_bytes: number };
  running: {
    run_id: string; process_id: string; process_name: string; holopod_id: string | null;
    source_id: string | null;
    state: RunState; apps: string[]; started_at: string | null; duration_seconds: number;
    decoded_fps: number; rows_written: number; bytes_written: number;
  }[];
  recent_events: EventRow[];
}

export interface SyncGroup {
  id: string; name: string; description: string; process_ids: string[];
  created_at: string; last_session: SyncSession | null;
}

export interface SyncSession {
  id: string; sync_group_id: string; state: string;
  requested_start_at: string | null; started_at: string | null; stopped_at: string | null;
  run_ids: string[];
  alignment: { runs: Record<string, any>; max_skew_s: number; note: string };
}

export interface ModelRow {
  id: string; name: string; task: string; framework: string; latest_version: string;
  active: boolean; description: string;
  versions: { version: string; compute: string; size_bytes: number; available: boolean }[];
}

export interface StorageDestination {
  id: string; name: string; provider: string; config: Record<string, unknown>;
  is_default: boolean; bytes_used: number; quota_bytes: number | null; has_credentials: boolean;
}

// -- session ---------------------------------------------------------------

const TOKEN_KEY = "holoxi.token";
const WORKSPACE_KEY = "holoxi.workspace";

export const session = {
  token: () => sessionStorage.getItem(TOKEN_KEY),
  workspace: () => sessionStorage.getItem(WORKSPACE_KEY),
  set(token: string, workspaceId: string) {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(WORKSPACE_KEY, workspaceId);
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(WORKSPACE_KEY);
  },
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public problems?: { field: string; message: string }[]) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const token = session.token();
  const workspace = session.workspace();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (workspace) headers.set("X-Workspace-Id", workspace);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const detail = body?.detail ?? response.statusText;
    throw new ApiError(response.status, typeof detail === "string" ? detail : "request failed", body?.problems);
  }
  return body as T;
}

export const api = {
  base: BASE,

  register: (payload: { email: string; password: string; full_name?: string; workspace_name?: string }) =>
    request<Session>("/api/auth/register", { method: "POST", body: JSON.stringify(payload) }),
  login: (email: string, password: string) =>
    request<Session>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => request<Session>("/api/auth/me"),

  dashboard: () => request<Dashboard>("/api/dashboard"),

  sources: () => request<Source[]>("/api/holopods"),
  createSource: (payload: Record<string, unknown>) =>
    request<Source>("/api/holopods", { method: "POST", body: JSON.stringify(payload) }),
  deleteSource: (id: string, force = false) =>
    request<{ deleted: string; name: string; detached_processes: number }>(
      `/api/holopods/${id}?force=${force}`, { method: "DELETE" }),
  testSource: (id: string) =>
    request<{ ok: boolean; error?: string; width?: number; height?: number; fps?: number }>(
      `/api/holopods/${id}/test`, { method: "POST" }),
  // <img> cannot send an Authorization header, so media URLs carry the token.
  previewUrl: (id: string) =>
    `${BASE}/api/holopods/${id}/preview.jpg?workspace_id=${session.workspace()}` +
    `&access_token=${session.token()}&t=${Date.now()}`,

  apps: () => request<AppManifest[]>("/api/apps"),

  processes: (query = "") => request<ProcessSummary[]>(`/api/processes${query}`),
  process: (id: string) => request<ProcessSummary>(`/api/processes/${id}`),
  createProcess: (payload: { name: string; description?: string; tags?: string[]; graph: ProcessGraph }) =>
    request<ProcessSummary>("/api/processes", { method: "POST", body: JSON.stringify(payload) }),
  updateProcess: (id: string, payload: Record<string, unknown>) =>
    request<ProcessSummary>(`/api/processes/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  duplicateProcess: (id: string) =>
    request<ProcessSummary>(`/api/processes/${id}/duplicate`, { method: "POST" }),
  deleteProcess: (id: string, purgeData = false) =>
    request<{ deleted: string; name: string; runs: number; objects_removed: number }>(
      `/api/processes/${id}?purge_data=${purgeData}`, { method: "DELETE" }),
  archiveProcess: (id: string) =>
    request<ProcessSummary>(`/api/processes/${id}/archive`, { method: "POST" }),
  versions: (id: string) => request<any[]>(`/api/processes/${id}/versions`),
  start: (id: string) =>
    request<RunSummary>(`/api/processes/${id}/start`, { method: "POST", body: JSON.stringify({}) }),

  runs: (query = "") => request<RunSummary[]>(`/api/runs${query}`),
  run: (id: string) => request<RunDetail>(`/api/runs/${id}`),
  stopRun: (id: string, reason = "") =>
    request<RunSummary>(`/api/runs/${id}/stop`, { method: "POST", body: JSON.stringify({ reason }) }),
  results: (id: string, since = 0) =>
    request<{ count: number; cursor: number; results: any[] }>(`/api/runs/${id}/results?since_id=${since}&limit=40`),
  artifacts: (id: string) => request<Artifact[]>(`/api/runs/${id}/artifacts`),
  // <a href> download — token in query, same as preview/export.
  zipArtifacts: (runId: string, opts: { kind?: string; appId?: string } = {}) => {
    const params = new URLSearchParams({
      workspace_id: session.workspace() ?? "",
      access_token: session.token() ?? "",
    });
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.appId) params.set("app_id", opts.appId);
    return `${BASE}/api/runs/${runId}/artifacts.zip?${params}`;
  },
  runEvents: (id: string) => request<EventRow[]>(`/api/runs/${id}/events`),
  runPreviewUrl: (id: string) =>
    `${BASE}/api/runs/${id}/preview.mjpg?workspace_id=${session.workspace()}` +
    `&access_token=${session.token()}`,
  runFrameUrl: (id: string) =>
    `${BASE}/api/runs/${id}/preview.jpg?workspace_id=${session.workspace()}` +
    `&access_token=${session.token()}&t=${Date.now()}`,

  browseArtifacts: (query = "") => request<Artifact[]>(`/api/data/artifacts${query}`),
  download: (artifact: Artifact) => `${BASE}${artifact.download_url}`,

  syncGroups: () => request<SyncGroup[]>("/api/sync-groups"),
  createSyncGroup: (payload: { name: string; description?: string; process_ids: string[] }) =>
    request<SyncGroup>("/api/sync-groups", { method: "POST", body: JSON.stringify(payload) }),
  startSyncGroup: (id: string) =>
    request<SyncSession>(`/api/sync-groups/${id}/start`, { method: "POST" }),
  syncSession: (id: string) => request<SyncSession>(`/api/sync-sessions/${id}`),
  stopSyncSession: (id: string) => request<SyncSession>(`/api/sync-sessions/${id}/stop`, { method: "POST" }),
  deleteSyncSession: (id: string) =>
    request<{ deleted: string }>(`/api/sync-sessions/${id}`, { method: "DELETE" }),
  deleteSyncGroup: (id: string) =>
    request<{ deleted: string; name: string; sessions: number }>(
      `/api/sync-groups/${id}`, { method: "DELETE" }),
  // Browser navigations (<a href>) cannot set Authorization, so the token
  // travels as a query param — same pattern as preview URLs.
  exportSession: (id: string) =>
    `${BASE}/api/sync-sessions/${id}/export?mode=combined` +
    `&workspace_id=${session.workspace()}&access_token=${session.token()}`,

  models: () => request<ModelRow[]>("/api/models"),
  createModel: (payload: Record<string, unknown>) =>
    request<ModelRow>("/api/models", { method: "POST", body: JSON.stringify(payload) }),

  storage: () => request<StorageDestination[]>("/api/storage-destinations"),
  createStorage: (payload: Record<string, unknown>) =>
    request<StorageDestination>("/api/storage-destinations", { method: "POST", body: JSON.stringify(payload) }),

  workers: () => request<WorkerRow[]>("/api/workers"),
  events: (query = "") => request<EventRow[]>(`/api/events${query}`),
  clearEvents: (severity = "") =>
    request<{ cleared: number; severity: string | null }>(
      `/api/events${severity ? `?severity=${encodeURIComponent(severity)}` : ""}`,
      { method: "DELETE" },
    ),
  audit: () => request<any[]>("/api/audit-logs"),
  clearAudit: () =>
    request<{ cleared: number }>("/api/audit-logs", { method: "DELETE" }),
};
